import React from "react";
import { ColumnsType } from "antd/es/table";
import { TFunction } from "i18next";

import LicenseStatusDot from "~/shared/ui/LicenseStatusDot";
import { applySortToColumns } from "~/shared/ui/TableV2";
import { InfoTooltip } from "~/shared/ui/Tooltip";

import {
  ILicenseOrganizationRow,
  LICENSE_ORGANIZATION_TOTALS_ROW_ID,
} from "./types";

export const LICENSE_ORGANIZATION_SORTABLE_COLUMNS = [
  "positionCode",
  "positionName",
  "availabilityColor",
  "totalQuantity",
  "assignedQuantity",
  "frozenQuantity",
  "freeQuantity",
  "expiredQuantity",
];

export const buildLicenseOrganizationColumns = (
  t: TFunction,
  sortField?: string,
  sortType?: string,
  licenseType?: string,
): ColumnsType<ILicenseOrganizationRow> => {
  const columns: ColumnsType<ILicenseOrganizationRow> = [
    {
      title: t("licenseOrganization.table.positionCode"),
      dataIndex: "positionCode",
      key: "positionCode",
      width: 110,
    },
    {
      title: t("licenseOrganization.table.positionName"),
      dataIndex: "positionName",
      key: "positionName",
      width: 260,
    },
    {
      title: t("licenseOrganization.table.licenseType"),
      dataIndex: "licenseType",
      key: "licenseType",
      width: 170,
      render: (_, record) =>
        record.id === LICENSE_ORGANIZATION_TOTALS_ROW_ID
          ? t("trainingManagement.licenseTypes.all")
          : licenseType && licenseType !== "all"
            ? t(`trainingManagement.licenseTypes.${licenseType}`)
            : t("trainingManagement.filter.all"),
    },
    {
      title: (
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          {t("licenseOrganization.table.overallStatus")}
          <InfoTooltip
            content={t("trainingManagement.table.overallStatusLegend")}
            placement="bottom"
          />
        </span>
      ),
      dataIndex: "availabilityColor",
      key: "availabilityColor",
      width: 130,
      render: (value: ILicenseOrganizationRow["availabilityColor"]) => (
        <LicenseStatusDot color={value} />
      ),
    },
    {
      title: t("licenseOrganization.table.statusGroup"),
      className: "licenseOrgStatusGroupHeader",
      children: [
        {
          title: t("licenseOrganization.table.total"),
          dataIndex: "totalQuantity",
          key: "totalQuantity",
          width: 160,
          align: "center",
          className: "licenseOrgStatusGroupCell licenseOrgStatusGroupCellFirst",
        },
        {
          title: t("trainingManagement.table.issuedInDealerPassport"),
          dataIndex: "assignedQuantity",
          key: "assignedQuantity",
          width: 150,
          align: "center",
          className: "licenseOrgStatusGroupCell",
        },
        {
          title: t("licenseOrganization.table.used"),
          dataIndex: "usedQuantity",
          key: "usedQuantity",
          width: 130,
          align: "center",
          className: "licenseOrgStatusGroupCell",
        },
        {
          title: t("licenseOrganization.table.frozen"),
          dataIndex: "frozenQuantity",
          key: "frozenQuantity",
          width: 120,
          align: "center",
          className: "licenseOrgStatusGroupCell",
        },
        {
          title: t("licenseOrganization.table.free"),
          dataIndex: "freeQuantity",
          key: "freeQuantity",
          width: 110,
          align: "center",
          className: "licenseOrgStatusGroupCell",
        },
        {
          title: t("licenseOrganization.table.expired"),
          dataIndex: "expiredQuantity",
          key: "expiredQuantity",
          width: 120,
          align: "center",
          className: "licenseOrgStatusGroupCell",
        },
      ],
    },
  ];

  return applySortToColumns(
    columns,
    sortField,
    sortType,
    LICENSE_ORGANIZATION_SORTABLE_COLUMNS,
  );
};
